import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Label,
  Icon,
  Header,
  Pagination,
  Dropdown,
  Grid,
  Image,
  Button,
  Modal,
  Popup,
} from "semantic-ui-react";
import PropTypes from "prop-types";
import {
  CONFIG,
  calculateDaysOverdue,
  getSortValue,
  formatCurrency,
  formatDate,
} from "./fineHistoryUtils";
import { fineApi } from "@api/fine/fine";

const useSortedData = (data, sortColumn, sortDirection) =>
  useMemo(() => {
    const filtered = data
      .filter((fine) => fine.status !== "active")
      .sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    return { filteredData: filtered, total: filtered.length };
  }, [data, sortColumn, sortDirection]);

const usePaginatedData = (data, total, itemsPerPage, activePage) =>
  useMemo(() => {
    const totalPages = Math.ceil(total / itemsPerPage);
    const startIndex = (activePage - 1) * itemsPerPage;
    const currentPageData = data.slice(startIndex, startIndex + itemsPerPage);
    return {
      totalPages,
      startIndex,
      endIndex: startIndex + itemsPerPage,
      currentPageData,
    };
  }, [data, total, itemsPerPage, activePage]);

const ReceiptImageModal = ({ imageUrl, open, onClose }) => {
  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        <Icon name="file image" />
        Payment Receipt
      </Modal.Header>
      <Modal.Content>
        <div style={{ textAlign: "center" }}>
          <Image src={imageUrl} fluid />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

ReceiptImageModal.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const FineTableHeader = ({ sortColumn, sortDirection, onSort }) => {
  const columns = [
    { key: "patron_name", text: "Patron", column: "patron" },
    { key: "document_title", text: "Document", column: "document" },
    { key: "item_barcode", text: "Barcode", column: "barcode" },
    { key: "due_date", text: "Due Date", column: "dueDate" },
    { key: "days_overdue", text: "Days Overdue", column: "daysOverdue" },
    { key: "total_fine", text: "Fine Amount", column: "fineAmount" },
  ];

  const headerStyle = (column, isActive = false) => ({
    ...CONFIG.baseFont,
    width: CONFIG.columnWidths[column],
    fontWeight: "600",
    backgroundColor: "#f8f9fa",
    cursor: column !== "receipt" ? "pointer" : "default",
    color: isActive ? "#007bff" : "#495057",
  });

  return (
    <Table.Header>
      <Table.Row>
        {columns.map(({ key, text, column }) => {
          const isActive = sortColumn === key;
          const icon = isActive
            ? sortDirection === "asc"
              ? "caret up"
              : "caret down"
            : "sort";
          return (
            <Table.HeaderCell
              key={key}
              style={headerStyle(column, isActive)}
              onClick={() => onSort(key)}
            >
              <span>{text}</span>
              <Icon
                name={icon}
                style={{ marginLeft: "4px", opacity: isActive ? 1 : 0.5 }}
              />
            </Table.HeaderCell>
          );
        })}
        <Table.HeaderCell style={headerStyle("status")}>
          Status
        </Table.HeaderCell>
        <Table.HeaderCell style={headerStyle("paymentRef")}>
          Payment Ref
        </Table.HeaderCell>
        <Table.HeaderCell style={headerStyle("receipt")}>
          Receipt
        </Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  );
};

const FineTableRow = ({ fine, index, fineDetails, onViewReceipt }) => {
  const daysOverdue = fine.due_date
    ? calculateDaysOverdue(fine.due_date, fine.return_date)
    : 0;

  const cellStyle = (column, extra = {}) => ({
    ...CONFIG.baseFont,
    width: CONFIG.columnWidths[column],
    padding: "14px 10px",
    verticalAlign: "middle",
    borderRight: column !== "receipt" ? "1px solid #dee2e6" : "none",
    ...extra,
  });

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    // Safely access environment variable with fallback
    const baseUrl = process.env.REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL;

    const cleanPath = imageUrl.startsWith("/")
      ? imageUrl.substring(1)
      : imageUrl;
    return `${baseUrl}/${cleanPath}`;
  };

  const renderReceiptCell = () => {
    const fineDetail = fineDetails[fine.id];
    const imageUrl = fineDetail?.image_url || fineDetail?.imageUrl;
    const fullImageUrl = getFullImageUrl(imageUrl);

    if (!fullImageUrl) {
      return (
        <div style={{ textAlign: "center", color: "#999", fontSize: "0.85em" }}>
          <Icon name="file outline" />
          <div>No Uploaded Receipt</div>
        </div>
      );
    }

    return (
      <div style={{ textAlign: "center" }}>
        <Popup
          content="Click to view receipt"
          trigger={
            <Image
              src={fullImageUrl}
              size="tiny"
              style={{
                cursor: "pointer",
                border: "1px solid #f4b952",
                borderRadius: "4px",
                maxHeight: "60px",
                maxWidth: "80px",
                objectFit: "cover",
                margin: "0 auto 4px",
                display: "block",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onViewReceipt(fullImageUrl);
              }}
              onError={(e) => {
                e.target.style.display = "none";
                const parent = e.target.parentElement;
                if (parent) {
                  parent.innerHTML =
                    '<div style="color: #999; font-size: 0.85em; text-align: center;"><i class="icon file outline"></i><div>Image error</div></div>';
                }
              }}
            />
          }
        />
        <div
          style={{
            display: "flex",
            gap: "4px",
            justifyContent: "center",
            marginTop: "4px",
          }}
        >
          <Button
            size="mini"
            basic
            icon
            onClick={(e) => {
              e.stopPropagation();
              onViewReceipt(fullImageUrl);
            }}
          >
            <Icon name="eye" />
          </Button>
        </div>
      </div>
    );
  };

  const fineDetail = fineDetails[fine.id];

  return (
    <Table.Row
      style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc" }}
    >
      <Table.Cell style={cellStyle("patron")}>
        <div style={{ fontWeight: "600", color: "#212529" }}>
          {fine.patron_name || "Unknown Patron"}
        </div>
        <div
          style={{ fontSize: "12px", color: "#6c757d", fontStyle: "italic" }}
        >
          {fine.patron_email || "No email"}
        </div>
        {fineDetail?.or_number && (
          <div
            style={{
              fontSize: "12px",
              color: "#f4b952",
              marginTop: "4px",
              fontWeight: "bold",
            }}
          >
            OR Number: {fineDetail.or_number}
          </div>
        )}
      </Table.Cell>

      <Table.Cell style={cellStyle("document")}>
        <span style={{ wordBreak: "break-word" }}>
          {fine.document_title || "Unknown Document"}
        </span>
      </Table.Cell>

      <Table.Cell style={cellStyle("barcode", { textAlign: "center" })}>
        {fine.item_barcode || "N/A"}
      </Table.Cell>

      <Table.Cell style={cellStyle("dueDate", { textAlign: "center" })}>
        {formatDate(fine.due_date)}
      </Table.Cell>

      <Table.Cell style={cellStyle("daysOverdue", { textAlign: "center" })}>
        {daysOverdue} days
      </Table.Cell>

      <Table.Cell style={cellStyle("fineAmount", { textAlign: "right" })}>
        {formatCurrency(fine.total_fine)}
      </Table.Cell>

      <Table.Cell style={cellStyle("status", { textAlign: "center" })}>
        <Label
          color={CONFIG.statusColors[fine.status] || "grey"}
          style={{ fontSize: "11px", padding: "6px 12px" }}
        >
          {fine.status
            ? fine.status.charAt(0).toUpperCase() + fine.status.slice(1)
            : "Unknown"}
        </Label>
      </Table.Cell>

      <Table.Cell style={cellStyle("paymentRef", { textAlign: "left" })}>
        {fine.payment_reference ? (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "6px 8px",
              borderRadius: "4px",
              border: "1px solid #dee2e6",
              wordBreak: "break-word",
              whiteSpace: "normal",
            }}
          >
            {fine.payment_reference}
          </div>
        ) : (
          <i style={{ color: "#6c757d" }}>No reference</i>
        )}
      </Table.Cell>

      <Table.Cell style={cellStyle("receipt", { textAlign: "center" })}>
        {renderReceiptCell()}
      </Table.Cell>
    </Table.Row>
  );
};

const FineHistoryTab = ({ data }) => {
  const { hits: fineHistory } = data;
  const [activePage, setActivePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState("due_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [fineDetails, setFineDetails] = useState({});
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { filteredData, total } = useSortedData(
    fineHistory,
    sortColumn,
    sortDirection
  );
  const { totalPages, startIndex, endIndex, currentPageData } =
    usePaginatedData(filteredData, total, itemsPerPage, activePage);

  useEffect(() => {
    const fetchFineDetails = async () => {
      if (fineHistory.length === 0) {
        return;
      }

      try {
        // Get unique fine IDs
        const fineIds = [
          ...new Set(fineHistory.map((f) => f.id).filter(Boolean)),
        ];

        const response = await fineApi.getBatch(fineIds);

        // Build a map for easy lookup by ID
        const detailsMap = {};
        response.data.hits.forEach((fine) => {
          detailsMap[fine.id] = fine;
        });

        setFineDetails(detailsMap);
      } catch (error) {
        setFineDetails({});
      }
    };

    fetchFineDetails();
  }, [fineHistory]);

  const handleViewReceipt = (imageUrl) => {
    setSelectedReceipt(imageUrl);
    setModalOpen(true);
  };

  if (total === 0) {
    return (
      <Header as="h3" color="grey" style={CONFIG.baseFont}>
        No Fine History Found
      </Header>
    );
  }

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setActivePage(1);
  };

  return (
    <>
      <div style={CONFIG.baseFont}>
        <Grid>
          <Grid.Column width={13}>
            <Header as="h3" style={{ fontSize: "18px", fontWeight: "600" }}>
              Fine History
            </Header>
          </Grid.Column>
          <Grid.Column width={3} textAlign="right">
            <Dropdown
              selection
              options={CONFIG.itemsPerPageOptions}
              value={itemsPerPage}
              onChange={(e, { value }) => {
                setItemsPerPage(value);
                setActivePage(1);
              }}
            />
          </Grid.Column>
        </Grid>

        <Table celled striped selectable>
          <FineTableHeader
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <Table.Body>
            {currentPageData.map((fine, i) => (
              <FineTableRow
                key={fine.id}
                fine={fine}
                index={i}
                fineDetails={fineDetails}
                onViewReceipt={handleViewReceipt}
              />
            ))}
          </Table.Body>
        </Table>

        <Grid>
          <Grid.Column width={8}>
            Showing {startIndex + 1} to {Math.min(endIndex, total)} of {total}{" "}
            records
          </Grid.Column>
          <Grid.Column width={8} textAlign="right">
            {totalPages > 1 && (
              <Pagination
                activePage={activePage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => setActivePage(activePage)}
              />
            )}
          </Grid.Column>
        </Grid>
      </div>

      {selectedReceipt && (
        <ReceiptImageModal
          imageUrl={selectedReceipt}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

FineTableHeader.propTypes = {
  sortColumn: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf(["asc", "desc"]).isRequired,
  onSort: PropTypes.func.isRequired,
};

FineTableRow.propTypes = {
  fine: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    patron_name: PropTypes.string,
    patron_email: PropTypes.string,
    document_title: PropTypes.string,
    item_barcode: PropTypes.string,
    due_date: PropTypes.string,
    return_date: PropTypes.string,
    total_fine: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    payment_reference: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  fineDetails: PropTypes.object.isRequired,
  onViewReceipt: PropTypes.func.isRequired,
};

FineHistoryTab.propTypes = {
  data: PropTypes.shape({
    hits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        patron_name: PropTypes.string,
        patron_email: PropTypes.string,
        document_title: PropTypes.string,
        item_barcode: PropTypes.string,
        due_date: PropTypes.string,
        return_date: PropTypes.string,
        total_fine: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        payment_reference: PropTypes.string,
      })
    ).isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
};

export default FineHistoryTab;
